import type { LocationQuery } from 'vue-router';

/**
 * Parse URL query parameters into filter values
 * @param query - The route query object
 * @param filterProperties - The filter properties to parse
 * @returns An array of parsed filter values
 */
export function parseFiltersFromQuery(
  query: LocationQuery, 
  filterProperties: FilterProperties[]
): FilterValue[] {
  const filtersFromUrl: FilterValue[] = [];
  
  // Process query parameters and create filter objects
  filterProperties.forEach(filterProp => {
    const filterType = filterProp.type;
    const filterId = filterProp.id || '';
    
    // Handle daterange and timerange types separately
    if (filterType === 'daterange' || filterType === 'timerange') {
      const rangeKey = filterId;
      const notRangeKey = `not_${filterId}`;
      
      // Check for both regular and excluded ranges
      [
        { key: rangeKey, isExcluded: false },
        { key: notRangeKey, isExcluded: true }
      ].forEach(({ key, isExcluded }) => {
        if (query[key]) {
          // Handle both single value and array of values
          const values = Array.isArray(query[key]) 
            ? query[key] as string[] 
            : [query[key] as string];
          
          values.forEach(value => {
            // Split the range value using the separator
            const [start, end] = String(value || '').split('|');
            
            filtersFromUrl.push({
              id: filterId,
              value: [
                String(start || ''),
                String(end || '')
              ],
              exclude: isExcluded,
              regex: false
            });
          });
        }
      });
    } else {
      // Handle other filter types (select, text, etc.)
      // Check for regular filter parameters
      if (query[filterId]) {
        // Handle both single value and array of values
        const values = Array.isArray(query[filterId]) 
          ? query[filterId] as string[] 
          : [query[filterId] as string];
        
        values.forEach(value => {
          filtersFromUrl.push({
            id: filterId,
            value: String(value || ''),
            exclude: false,
            regex: false
          });
        });
      }
      
      // Check for regex filter parameters
      if (query[`${filterId}_regex`]) {
        // Handle both single value and array of values
        const values = Array.isArray(query[`${filterId}_regex`]) 
          ? query[`${filterId}_regex`] as string[] 
          : [query[`${filterId}_regex`] as string];
        
        values.forEach(value => {
          filtersFromUrl.push({
            id: filterId,
            value: String(value || ''),
            exclude: false,
            regex: true
          });
        });
      }
      
      // Check for excluded filter parameters
      if (query[`not_${filterId}`]) {
        // Handle both single value and array of values
        const values = Array.isArray(query[`not_${filterId}`]) 
          ? query[`not_${filterId}`] as string[] 
          : [query[`not_${filterId}`] as string];
        
        values.forEach(value => {
          filtersFromUrl.push({
            id: filterId,
            value: String(value || ''),
            exclude: true,
            regex: false
          });
        });
      }
      
      // Check for excluded regex filter parameters
      if (query[`not_${filterId}_regex`]) {
        // Handle both single value and array of values
        const values = Array.isArray(query[`not_${filterId}_regex`]) 
          ? query[`not_${filterId}_regex`] as string[] 
          : [query[`not_${filterId}_regex`] as string];
        
        values.forEach(value => {
          filtersFromUrl.push({
            id: filterId,
            value: String(value || ''),
            exclude: true,
            regex: true
          });
        });
      }
    }
  });
  
  return filtersFromUrl;
}

/**
 * Query keys TableView used to store in route.query. They belong in the fragment.
 */
export function accountFilterQueryKeys(filterProperties: FilterProperties[]): Set<string> {
  const keys = new Set<string>(['search']);
  filterProperties.forEach((filterProp) => {
    const filterId = filterProp.id || '';
    if (!filterId) return;
    keys.add(filterId);
    keys.add(`not_${filterId}`);
    keys.add(`${filterId}_regex`);
    keys.add(`not_${filterId}_regex`);
  });
  return keys;
}

/**
 * Convert filter values (and the table search box) to a query string.
 * The SPA stores this in the URL fragment. Background API calls append it
 * as a normal GET query string.
 */
export function filtersToSearchParams(
  filters: FilterValue[],
  filterProperties: FilterProperties[],
  search = '',
): URLSearchParams {
  const searchParams = new URLSearchParams();

  filters.forEach((filter) => {
    const filterType = filterProperties.find(f => f.id === filter.id)?.type || 'select';
    const paramPrefix = filter.exclude ? 'not_' : '';
    const filterId = filter.id || '';

    // Handle daterange and timerange types
    if (filterType === 'daterange' || filterType === 'timerange') {
      if (Array.isArray(filter.value) && filter.value.length > 0) {
        const start = filter.value[0] || '';
        const end = filter.value[1] || '';

        if (start || end) {
          const rangeValue = `${start}|${end}`;
          searchParams.append(`${paramPrefix}${filterId}`, rangeValue);
        }
      }
    } else if (filter.value) {
      const regexSuffix = filter.regex ? '_regex' : '';
      searchParams.append(`${paramPrefix}${filterId}${regexSuffix}`, String(filter.value));
    }
  });

  if (search) {
    searchParams.set('search', search);
  }

  return searchParams;
}

/**
 * Convert filter values to a query record.
 */
export function filtersToQueryParams(
  filters: FilterValue[],
  filterProperties: FilterProperties[]
): Record<string, string | string[]> {
  return searchParamsToRecord(filtersToSearchParams(filters, filterProperties));
}

export function searchParamsToLocationQuery(params: URLSearchParams): LocationQuery {
  const query: LocationQuery = {};
  const seen = new Set<string>();
  for (const key of params.keys()) {
    if (seen.has(key)) continue;
    seen.add(key);
    const values = params.getAll(key);
    query[key] = values.length > 1 ? values : (values[0] ?? '');
  }
  return query;
}

function searchParamsToRecord(params: URLSearchParams): Record<string, string | string[]> {
  const record: Record<string, string | string[]> = {};
  const seen = new Set<string>();
  for (const key of params.keys()) {
    if (seen.has(key)) continue;
    seen.add(key);
    const values = params.getAll(key);
    record[key] = values.length > 1 ? values : (values[0] ?? '');
  }
  return record;
}

/**
 * Encode a filter query string for the URL fragment.
 *
 * Vue Router passes the hash through `encodeURI`, which encodes `%`. On read,
 * `route.hash` is that value decoded once, so it is still `encodeURIComponent`
 * of the query string. `filterQueryFromHash` decodes that remaining layer.
 */
export function encodeFilterHash(queryString: string): string {
  if (!queryString) return '';
  return '#' + encodeURIComponent(queryString);
}

/** Canonical filter query string from a route hash (`#` stripped). */
export function filterQueryFromHash(hash: string): string {
  if (!hash) return '';
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export function searchParamsFromHash(hash: string): URLSearchParams {
  return new URLSearchParams(filterQueryFromHash(hash));
}
